<template>
    <b-modal id="evidence-modal" :visible="visible" :title="modalTitle" size="md" hide-footer @hide="$emit('close')">
        <div class="evidence-modal-content">
            <p class="evidence-hint text-muted small mb-3">
                <i class="fas fa-info-circle mr-1"></i>
                증빙자료는 첨부 또는 삭제 시 바로 저장됩니다.
            </p>
            <p v-if="readOnly" class="text-muted small mb-3">
                <i class="fas fa-lock mr-1"></i>
                정산완료된 워크플로우에 연결된 증빙은 수정/삭제할 수 없습니다.
            </p>

            <div class="evidence-list mb-3">
                <div v-if="evidenceLoading" class="text-muted small py-3 text-center">
                    <i class="fas fa-spinner fa-spin mr-1"></i> 로딩 중...
                </div>
                <template v-else>
                    <!-- 기존 파일 목록 -->
                    <ul v-if="evidenceFiles.length > 0 && !selectedFile" class="list-unstyled mb-2">
                        <li
                            v-for="f in evidenceFiles"
                            :key="f.fileIdx"
                            class="evidence-item d-flex align-items-center justify-content-between py-2 px-2 rounded border mb-2"
                        >
                            <div class="evidence-info flex-grow-1 min-w-0 d-flex align-items-center">
                                <span class="font-weight-medium">{{ f.title || f.fileName || "파일" }}</span>
                                <span v-if="getEvidenceTypeLabel(f.evidenceType)" class="badge badge-light ml-1">
                                    {{ getEvidenceTypeLabel(f.evidenceType) }}
                                </span>
                                <span class="text-muted small ml-1">({{ formatFileSize(f.fileSize) }})</span>
                                <span class="ml-1 evidence-info-icon text-muted" :title="getFileTooltip(f)">
                                    <i class="fas fa-info-circle"></i>
                                </span>
                            </div>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-danger"
                                :disabled="readOnly || deletingFileIdx === f.fileIdx"
                                @click="handleEvidenceDelete(f)"
                            >
                                <i v-if="deletingFileIdx === f.fileIdx" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-trash-alt"></i>
                            </button>
                        </li>
                    </ul>

                    <!-- 클릭하여 파일 선택 영역 (pending 파일 없을 때) -->
                    <div
                        v-if="!selectedFile"
                        class="file-select-zone"
                        role="button"
                        tabindex="0"
                        :aria-disabled="readOnly ? 'true' : 'false'"
                        @click="!readOnly && triggerFileSelect()"
                        @keydown.enter.space.prevent="!readOnly && triggerFileSelect()"
                    >
                        <template v-if="evidenceFiles.length === 0">
                            <i class="fas fa-cloud-upload-alt fa-2x text-muted mb-2"></i>
                            <p class="mb-0 text-muted small">첨부된 증빙자료가 없습니다.</p>
                            <p class="mb-0 text-muted small">{{ readOnly ? "정산완료 상태에서는 파일을 추가할 수 없습니다." : "클릭하여 파일을 선택하세요" }}</p>
                        </template>
                        <template v-else>
                            <i class="fas fa-plus-circle text-muted mb-1"></i>
                            <p class="mb-0 text-muted small">{{ readOnly ? "정산완료 상태에서는 파일을 추가할 수 없습니다." : "클릭하여 파일 추가" }}</p>
                        </template>
                    </div>

                    <!-- 파일 선택 후: 입력 폼 + 업로드 -->
                    <div v-if="selectedFile" class="pending-upload-form rounded mb-2">
                        <div class="selected-file-display">
                            <i :class="[selectedFileIcon, 'selected-file-icon mr-2']"></i>
                            <span class="selected-file-name">{{ selectedFile.name }}</span>
                            <span v-if="selectedFile.size" class="text-muted small ml-1">
                                ({{ formatFileSize(selectedFile.size) }})
                            </span>
                        </div>
                        <b-form-group
                            v-if="evidenceTypeOptions.length > 1"
                            label="증빙유형"
                            label-size="sm"
                            class="mb-1"
                        >
                            <b-form-select
                                v-model="uploadEvidenceType"
                                size="sm"
                                :options="evidenceTypeOptions"
                                value-field="value"
                                text-field="text"
                            />
                        </b-form-group>
                        <b-form-group label="표시명" label-size="sm" class="mb-1">
                            <b-form-input
                                v-model="uploadTitle"
                                size="sm"
                                placeholder="예: 1월 전기 청구서 (선택)"
                                maxlength="200"
                                :state="uploadTitleState"
                            />
                            <small class="text-muted">{{ (uploadTitle || "").length }}/200</small>
                        </b-form-group>
                        <b-form-group label="비고" label-size="sm" class="mb-1">
                            <b-form-textarea
                                v-model="uploadRemarks"
                                size="sm"
                                placeholder="비고 (선택, 최대 1000자)"
                                rows="3"
                                maxlength="1000"
                                no-resize
                                :state="uploadRemarksState"
                            />
                            <small class="text-muted">{{ (uploadRemarks || "").length }}/1000</small>
                        </b-form-group>
                        <div class="d-flex">
                            <b-button size="sm" variant="primary" :disabled="uploading" @click="doUpload" class="mr-2">
                                <i v-if="uploading" class="fas fa-spinner fa-spin mr-1"></i>
                                <i v-else class="fas fa-upload mr-1"></i>
                                파일 첨부
                            </b-button>
                            <b-button size="sm" variant="outline-secondary" :disabled="uploading" @click="clearPending">
                                취소
                            </b-button>
                        </div>
                    </div>
                </template>
            </div>

            <input
                ref="evidenceFileInput"
                type="file"
                class="d-none"
                accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png"
                :disabled="readOnly"
                @change="handleFileSelected"
            />

            <div class="modal-footer-actions d-flex justify-content-end">
                <CarbonButton variant="secondary" @click="$emit('close')">닫기</CarbonButton>
            </div>
        </div>
    </b-modal>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "@/submodules/carbon-plugin/codes";
import CarbonButton from "@/submodules/carbon-plugin/components/common/CarbonButton.vue";

export default {
    name: "EvidenceModal",
    components: { CarbonButton },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        /** 범용: 증빙 구분 (ACTIVITY, FACTOR, REPORT, ORG, FACILITY) */
        evidenceCategory: {
            type: String,
            default: "ACTIVITY",
        },
        /** 범용: 대상 엔터티 PK (activityIdx, valueIdx 등). 미지정 시 row에서 추론 */
        entityIdx: {
            type: Number,
            default: null,
        },
        /** 하위호환: row.activityIdx 또는 row.valueIdx 사용 */
        row: {
            type: Object,
            default: () => ({}),
        },
        facilities: {
            type: Array,
            default: () => [],
        },
        /** 범용: 모달 제목 오버라이드 (미지정 시 row 기반 자동 생성) */
        modalTitleOverride: {
            type: String,
            default: "",
        },
        /** 읽기 전용 모드: 업로드/삭제 비활성 (예: 정산완료) */
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            evidenceFiles: [],
            evidenceLoading: false,
            uploading: false,
            deletingFileIdx: null,
            selectedFile: null,
            uploadTitle: "",
            uploadRemarks: "",
            uploadEvidenceType: null,
        };
    },
    computed: {
        entityIdxResolved() {
            if (this.entityIdx != null) return this.entityIdx;
            if (this.evidenceCategory === "ACTIVITY") return this.row?.activityIdx ?? null;
            if (this.evidenceCategory === "FACTOR") return this.row?.valueIdx ?? null;
            if (this.evidenceCategory === "REPORT") return this.row?.reportIdx ?? null;
            if (this.evidenceCategory === "ORG") return this.row?.orgIdx ?? null;
            if (this.evidenceCategory === "FACILITY") return this.row?.facilityIdx ?? null;
            if (this.evidenceCategory === "COMPLIANCE") return this.row?.workflowIdx ?? null;
            return (
                this.row?.activityIdx ??
                this.row?.valueIdx ??
                this.row?.reportIdx ??
                this.row?.orgIdx ??
                this.row?.facilityIdx ??
                this.row?.workflowIdx ??
                null
            );
        },
        modalTitle() {
            if (this.modalTitleOverride) return this.modalTitleOverride;
            const period = this.formatPeriod(this.row?.period);
            const facilityName = this.getFacilityName(this.row?.facilityIdx);
            const category = this.row?.category || "-";
            return `증빙자료 - ${period} ${facilityName || ""} ${category}`.trim();
        },
        uploadTitleState() {
            const len = (this.uploadTitle || "").trim().length;
            return len > 200 ? false : null;
        },
        uploadRemarksState() {
            const len = (this.uploadRemarks || "").trim().length;
            return len > 1000 ? false : null;
        },
        selectedFileIcon() {
            if (!this.selectedFile?.name) return "fas fa-file";
            const ext = this.selectedFile.name.split(".").pop()?.toLowerCase();
            if (["xlsx", "xls"].includes(ext)) return "fas fa-file-excel";
            if (ext === "pdf") return "fas fa-file-pdf";
            if (["jpg", "jpeg", "png"].includes(ext)) return "fas fa-file-image";
            return "fas fa-file";
        },
        /** evidenceCategory에 따른 증빙유형 옵션 목록 */
        evidenceTypeOptions() {
            const map = {
                ACTIVITY: CarbonCodes.evdTypeActivity,
                FACTOR: CarbonCodes.evdTypeFactor,
                REPORT: CarbonCodes.evdTypeReport,
                ORG: CarbonCodes.evdTypeOrg,
                FACILITY: CarbonCodes.evdTypeFacility,
                COMPLIANCE: CarbonCodes.evdTypeCompliance,
            };
            const helper = map[this.evidenceCategory];
            if (!helper) return [];
            const list = helper.getList();
            return [{ value: null, text: "선택 안 함" }, ...list.map((c) => ({ value: c.value, text: c.text }))];
        },
    },
    watch: {
        visible(val) {
            if (val && this.entityIdxResolved != null) {
                this.loadEvidenceFiles();
            } else {
                this.evidenceFiles = [];
                this.selectedFile = null;
                this.uploadTitle = "";
                this.uploadRemarks = "";
                this.uploadEvidenceType = null;
            }
        },
    },
    methods: {
        formatPeriod(period) {
            if (!period) return "";
            const str = String(period);
            return str.length === 6 ? `${str.slice(0, 4)}-${str.slice(4, 6)}` : str;
        },
        getFacilityName(facilityIdx) {
            if (facilityIdx == null) return null;
            const f = this.facilities.find((x) => x.facilityIdx === facilityIdx);
            return f ? f.facilityName || f.facilityCode || null : null;
        },
        formatFileSize(bytes) {
            if (bytes == null || bytes === 0) return "0 B";
            const k = 1024;
            const sizes = ["B", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
        },

        getEvidenceTypeLabel(evidenceType) {
            if (!evidenceType) return "";
            const map = {
                ACTIVITY: CarbonCodes.evdTypeActivity,
                FACTOR: CarbonCodes.evdTypeFactor,
                REPORT: CarbonCodes.evdTypeReport,
                ORG: CarbonCodes.evdTypeOrg,
                FACILITY: CarbonCodes.evdTypeFacility,
                COMPLIANCE: CarbonCodes.evdTypeCompliance,
            };
            const helper = map[this.evidenceCategory];
            return helper ? helper.getLabel(evidenceType) : evidenceType;
        },
        getFileTooltip(f) {
            const parts = [];
            if (f.evidenceType) parts.push(`증빙유형: ${this.getEvidenceTypeLabel(f.evidenceType)}`);
            if (f.title) parts.push(`표시명: ${f.title}`);
            if (f.remarks) parts.push(`비고: ${f.remarks}`);
            return parts.length ? parts.join("\n") : "";
        },

        triggerFileSelect() {
            if (this.readOnly) return;
            this.$refs.evidenceFileInput?.click();
        },

        handleFileSelected(ev) {
            const input = ev.target;
            const file = input?.files?.[0];
            if (!file) return;
            this.selectedFile = file;
            this.uploadTitle = "";
            this.uploadRemarks = "";
            input.value = "";
        },

        clearPending() {
            this.selectedFile = null;
            this.uploadTitle = "";
            this.uploadRemarks = "";
            this.uploadEvidenceType = null;
        },

        async doUpload() {
            if (this.readOnly) {
                this.$bvToast.toast("정산완료 상태에서는 증빙을 변경할 수 없습니다.", {
                    variant: "warning",
                    toaster: "b-toaster-bottom-right",
                });
                return;
            }
            if (!this.selectedFile || this.entityIdxResolved == null) return;
            if (this.uploadTitleState === false || this.uploadRemarksState === false) {
                this.$bvToast.toast("표시명은 200자, 비고는 1000자 이하여야 합니다.", {
                    variant: "warning",
                    toaster: "b-toaster-bottom-right",
                });
                return;
            }
            this.uploading = true;
            try {
                const title = (this.uploadTitle || "").trim().slice(0, 200) || undefined;
                const remarks = (this.uploadRemarks || "").trim().slice(0, 1000) || undefined;
                const evidenceType = this.uploadEvidenceType || undefined;
                await carbonApi.evidenceFile.uploadEvidenceFile(
                    {
                        evidenceCategory: this.evidenceCategory,
                        entityIdx: this.entityIdxResolved,
                        evidenceType,
                        fileName: this.selectedFile.name,
                        title,
                        remarks,
                    },
                    this.selectedFile,
                );
                this.$bvToast.toast("증빙 파일이 업로드되었습니다.", {
                    variant: "success",
                    toaster: "b-toaster-bottom-right",
                });
                this.clearPending();
                await this.loadEvidenceFiles();
                this.$emit("updated");
            } catch (err) {
                console.error("[EvidenceModal] 증빙 업로드 실패:", err);
                const msg = err?.response?.data?.message || err?.message || "증빙 파일 업로드에 실패했습니다.";
                this.$bvToast.toast(msg, {
                    variant: "danger",
                    toaster: "b-toaster-bottom-right",
                });
            } finally {
                this.uploading = false;
            }
        },

        async loadEvidenceFiles() {
            if (this.entityIdxResolved == null) return;
            this.evidenceLoading = true;
            try {
                const res = await carbonApi.evidenceFile.getEvidenceFiles({
                    evidenceCategory: this.evidenceCategory,
                    entityIdx: this.entityIdxResolved,
                });
                const data = res?.data?.data || res?.data;
                this.evidenceFiles = data?.files || [];
            } catch (err) {
                console.error("[EvidenceModal] 증빙 목록 로드 실패:", err);
                this.$bvToast.toast("증빙 목록을 불러오는 중 오류가 발생했습니다.", {
                    variant: "danger",
                    toaster: "b-toaster-bottom-right",
                });
                this.evidenceFiles = [];
            } finally {
                this.evidenceLoading = false;
            }
        },

        async handleEvidenceDelete(file) {
            if (this.readOnly) {
                this.$bvToast.toast("정산완료 상태에서는 증빙을 삭제할 수 없습니다.", {
                    variant: "warning",
                    toaster: "b-toaster-bottom-right",
                });
                return;
            }
            if (!file?.fileIdx) return;
            const ok = await this.$bvModal.msgBoxConfirm(`"${file.title || file.fileName}" 파일을 삭제하시겠습니까?`, {
                title: "삭제 확인",
                okVariant: "danger",
                okTitle: "삭제",
                cancelTitle: "취소",
                centered: true,
            });
            if (!ok) return;
            this.deletingFileIdx = file.fileIdx;
            try {
                await carbonApi.evidenceFile.deleteEvidenceFile(file.fileIdx);
                this.$bvToast.toast("증빙 파일이 삭제되었습니다.", {
                    variant: "success",
                    toaster: "b-toaster-bottom-right",
                });
                await this.loadEvidenceFiles();
                this.$emit("updated");
            } catch (err) {
                const status = err?.response?.status;
                let msg = err?.response?.data?.message || err?.message || "증빙 파일 삭제에 실패했습니다.";
                if (status === 404) msg = "이미 삭제된 파일입니다.";
                else if (status === 409)
                    msg = err?.response?.data?.message || "확정된 워크플로우에 연결된 증빙 파일은 삭제할 수 없습니다.";
                this.$bvToast.toast(msg, {
                    variant: "danger",
                    toaster: "b-toaster-bottom-right",
                });
                if (status !== 404) await this.loadEvidenceFiles();
            } finally {
                this.deletingFileIdx = null;
            }
        },
    },
};
</script>

<style scoped>
.evidence-modal-content {
    padding: 8px 0;
}

.evidence-hint {
    font-size: 12px;
}

.evidence-item {
    background: #f8fafc;
}

.evidence-info {
    font-size: 13px;
}

.evidence-info-icon {
    cursor: help;
    opacity: 0.7;
}

.evidence-info-icon:hover {
    opacity: 1;
}

.file-select-zone {
    padding: 24px 16px;
    border: 2px dashed #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
}

.file-select-zone:hover {
    border-color: #52b788;
    background: #f0fdf4;
}

.file-select-zone:focus {
    outline: none;
    border-color: #52b788;
}

.pending-upload-form {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    padding: 16px;
}

.selected-file-display {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    margin-bottom: 10px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #52b788;
    border-radius: 4px;
}

.selected-file-icon {
    font-size: 14px;
    color: #64748b;
}

.selected-file-name {
    font-size: 13px;
    font-weight: 500;
    word-break: break-all;
}

.modal-footer-actions {
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}
</style>
